import { sneakyPromise } from '@/store/profiling';
import { getTronDisplayMedia } from '@/store/utils';
import ysFixWebmDuration from 'fix-webm-duration'; // eslint-disable-line

function getSupportedMimeTypes() {
  const possibleTypes = [
    'video/x-matroska;codecs=avc1,opus',
    'video/webm;codecs=h264,opus', // formally webm supports only vp8 and vp9 but browsers allow h264
    // 'video/webm;codecs=vp9,opus', //vp8 and vp9 will require ffmpeg recoding to ACC, so simple remuxing will not work
    // 'video/webm;codecs=vp8,opus',
  ];
  const supported = possibleTypes.filter(mimeType => MediaRecorder.isTypeSupported(mimeType));
  return supported;
}

// stop both mic and camera
function stopBothVideoAndAudio(stream) {
  console.log('stopBothVideoAndAudio');
  stream.getTracks().forEach((track) => {
    if (track.readyState === 'live') {
      track.stop();
    }
  });
  if (window.trackliRec.videoTag) {
    try {
      document.exitPictureInPicture();
    } catch (e) {
      console.log('Error existing PIP', e);
    }
    window.trackliRec.videoTag.remove();
    window.trackliRec.videoTag = null;
  }
}

function handleDataAvailable(event) {
  if (event.data.size > 0) {
    window.trackliRec.chunks.push(event.data);
  }
}

function blobToFile(theBlob, fileName) {
  // A Blob() is almost a File() - it's just missing the two properties below which we will add
  theBlob.lastModifiedDate = new Date(); // eslint-disable-line
  theBlob.name = fileName; // eslint-disable-line
  return theBlob;
}

export async function stopRecording() {
  if (!window.trackliRec) {
    return;
  }
  if (window.trackliRec.mediarec) {
    window.trackliRec.mediarec.stop();
    return;
  }
  if (!window.trackliRec.mediarec) {
    console.log('Creating end file 📽️');
    const {
      stoppedCallback, chunks, startTime, pausedDuration, pauseStartedAt,
    } = window.trackliRec;
    window.trackliRec = null;
    const activePauseDuration = pauseStartedAt ? Date.now() - pauseStartedAt : 0;
    const duration = Date.now() - startTime - pausedDuration - activePauseDuration;
    const buggyBlob = new Blob(chunks, {
      type: 'video/x-matroska',
    });
    const fixed = await ysFixWebmDuration(buggyBlob, duration, { logger: false });
    const file = blobToFile(fixed, 'video.mkv');
    stoppedCallback(file);
  }
}

export function pauseRecording() {
  window.trackliRec.mediarec.pause();
  window.trackliRec.pauseStartedAt = Date.now();
}

export function resumeRecording() {
  const recording = window.trackliRec;
  recording.pausedDuration += Date.now() - recording.pauseStartedAt;
  recording.pauseStartedAt = null;
  recording.mediarec.resume();
}

export async function cleanUpStreams() {
  if (window.trackliRec && window.trackliRec.webcamStream) {
    console.log('stopping webcamStream');
    stopBothVideoAndAudio(window.trackliRec.webcamStream);
    window.trackliRec.webcamStream = null;
  }
  console.log('Cleaning up streams, setting trackliRec to null');
  window.trackliRec = null;
}

export function requestWebcamPiP() {
  if (window.trackliRec && window.trackliRec.videoTag && window.trackliRec.videoTag.readyState >= 2) {
    try {
      return window.trackliRec.videoTag.requestPictureInPicture();
    } catch (error) {
      console.log('Picture-in-Picture not available:', error.message);
      return Promise.reject(error);
    }
  }
  return Promise.reject(new Error('Webcam video not available'));
}

export async function initializeStreams({ recordWebcam, recordMicrophone, videoEl }) {
  console.log('Initializing streams', { recordWebcam, recordMicrophone, videoEl });
  if (!window.trackliRec) {
    window.trackliRec = {
      chunks: [],
    };
  }
  window.trackliRec.recordMicrophone = recordMicrophone;
  if (window.trackliRec.webcamStream) {
    console.log('stopping webcamStream');
    stopBothVideoAndAudio(window.trackliRec.webcamStream);
    window.trackliRec.webcamStream = null;
  }
  if (!recordMicrophone && !recordWebcam) {
    return { ok: true };
  }
  try {
    const constraints = {
      audio: recordMicrophone ? {
        echoCancellation: true,
        noiseSuppression: true, // the user agent will attempt to obtain media with noise
        // suppression enabled or disabled as specified, if possible, but will not fail if this can't be done
      } : false, // don't pass this if user don't want audio
      video: recordWebcam ? ({
        width: 1024,
        height: 768,
        frameRate: {
          min: 20,
          ideal: 20,
          max: 30,
        },
      }) : false, // don't pass thus if user don't want camera
    };
    window.trackliRec.webcamStream = await navigator.mediaDevices.getUserMedia(constraints);
  } catch (error) {
    return { error };
  }
  if (recordWebcam) {
    const videoTag = document.createElement('video');
    window.trackliRec.videoTag = videoTag;
    videoTag.style = 'position: fixed; bottom:0; right:0; width: 1px; height: 1px';
    document.body.appendChild(videoTag);
    videoTag.muted = true;
    videoTag.autoplay = true;
    videoTag.srcObject = window.trackliRec.webcamStream; /* eslint no-param-reassign: ["error", { "props": false }] */
    videoTag.addEventListener('loadedmetadata', () => {
      // Try to request PiP but don't fail if user gesture is required
      try {
        videoTag.requestPictureInPicture();
      } catch (error) {
        console.log('Picture-in-Picture requires user gesture, will be available when recording starts');
      }
    }, { once: true });
  }
  return { ok: true };
}

async function downscaleToFullHd(inputStream) {
  // if width is less than 2000, don't downscale
  const track = inputStream.getVideoTracks()[0];
  const settings = track.getSettings();
  const inputWidth = settings.width;
  const inputHeight = settings.height;
  if (inputWidth < 2000) {
    console.log('🎥 Input stream is less than 2000px, not downscaling');
    return inputStream;
  }

  if (window.SCREENREC_SKIP_DOWNSCALE || true) { //for now disable resacling
    console.log('🎥 Skipping downscale due to SCREENREC_SKIP_DOWNSCALE');
    return inputStream;
  }

  // dispose of previous canvas
  console.log('Downscaling to Full HD 📽️');
  const aspectRatio = inputWidth / inputHeight;

  // Desired width (e.g. 1920px)
  const targetWidth = 1920;
  const targetHeight = Math.round(targetWidth / aspectRatio); // preserve aspect ratio

  // Create canvas
  if (!window.trackliRec.canvas) {
    window.trackliRec.canvas = document.createElement('canvas');
  }
  window.trackliRec.canvas.width = targetWidth;
  window.trackliRec.canvas.height = targetHeight;

  if (!window.trackliRec.video) {
    window.trackliRec.video = document.createElement('video');
  }

  window.trackliRec.video.srcObject = inputStream;
  window.trackliRec.video.muted = true; // prevent feedback
  await window.trackliRec.video.play();

  const ctx = window.trackliRec.canvas.getContext('2d');
  const fps = 24;

  // Downscale the video by drawing it to canvas
  function drawLoop() {
    ctx.drawImage(window.trackliRec.video, 0, 0, window.trackliRec.canvas.width, window.trackliRec.canvas.height);
    if (window.trackliRec) {
      requestAnimationFrame(drawLoop);
    }
  }

  requestAnimationFrame(drawLoop);

  await sneakyPromise((resolve) => {
    const checkFrame = () => {
      if (window.trackliRec.canvas.captureStream().getVideoTracks().length > 0) {
        resolve();
      } else {
        setTimeout(checkFrame, 10);
      }
    };
    checkFrame();
  });
  const downscaledStream = window.trackliRec.canvas.captureStream(fps);
  // Test before using:
  console.log('tracks:', downscaledStream.getTracks());
  console.log('video tracks:', downscaledStream.getVideoTracks());
  console.log('downscaledStream instanceof MediaStream:', downscaledStream instanceof MediaStream);
  console.log('downscaledStream.getVideoTracks().length:', downscaledStream.getVideoTracks().length);

  // Use downscaled stream
  return downscaledStream;
}

export async function startRecording(stoppedCallback) {
  try {
    const constraints = {
      video: {
        displaySurface: 'monitor',
        // HiDPI screens are captured at doubled size (a 2560x1440 display gives a 5120x2880
        // frame), and hardware H.264 refuses to encode anything wider than 4096, failing with
        // EncodingError right after start. Chromium scales the capture down for us.
        width: { max: 2560 },
        height: { max: 1440 },
        frameRate: {
          ideal: 20,
          max: 30,
        },
      },
    };
    if (window.APP_TRON && window.devicePlatform === 'darwin') {
      window.trackliRec.screenStream = await navigator.mediaDevices.getDisplayMedia(constraints);
    } else if (window.APP_TRON) {
      window.trackliRec.screenStream = await getTronDisplayMedia(constraints);
    } else {
      window.trackliRec.screenStream = await navigator.mediaDevices.getDisplayMedia(constraints);
    }
    console.log('🌊 got stream', window.trackliRec.screenStream);
  } catch (error) {
    return { error };
  }

  window.trackliRec.stoppedCallback = stoppedCallback;
  const supported = getSupportedMimeTypes();
  console.log('Supported mime types', supported);
  const options = {
    mimeType: supported[0],
    videoBitsPerSecond: 2.0 * 1e6, // 0.5 - unreadable texts, 2.5 default
    audioBitsPerSecond: 128e3, // 128e3 (128kbs)
  };

  console.log('Mime type', options, 'supported', supported);

  window.trackliRec.downscaledStream = await downscaleToFullHd(window.trackliRec.screenStream);

  window.trackliRec.screenStream.oninactive = () => {
    console.log('screenStream oninactive');
    stopRecording();
  };

  if (window.trackliRec.recordMicrophone) {
    window.trackliRec.downscaledStream = new MediaStream([
      window.trackliRec.downscaledStream.getVideoTracks()[0],
      window.trackliRec.webcamStream.getAudioTracks()[0],
    ]);
  }

  window.trackliRec.mediarec = new MediaRecorder(window.trackliRec.downscaledStream, options);
  window.trackliRec.mediarec.ondataavailable = handleDataAvailable;
  window.trackliRec.mediarec.start();
  const dispMediaSettingsMerged = window.trackliRec.mediarec
    .stream.getVideoTracks()[0].getSettings();
  console.log('merged with mike screenStream settings: ',
    dispMediaSettingsMerged);

  window.trackliRec.startTime = Date.now();
  window.trackliRec.pausedDuration = 0;
  window.trackliRec.pauseStartedAt = null;
  window.trackliRec.mediarec.onstop = () => {
    console.log('Onstop received');
    window.trackliRec.mediarec = null;
    console.log('stopping screenStream');
    stopBothVideoAndAudio(window.trackliRec.screenStream);
    if (window.trackliRec.webcamStream) {
      console.log('stopping webcamStream');
      stopBothVideoAndAudio(window.trackliRec.webcamStream);
    }
    stopRecording();
  };

  // Request Picture-in-Picture for webcam if available (user gesture context)
  if (window.trackliRec.videoTag && window.trackliRec.videoTag.readyState >= 2) {
    try {
      window.trackliRec.videoTag.requestPictureInPicture();
    } catch (error) {
      console.log('Picture-in-Picture not available:', error.message);
    }
  }

  return { ok: true };
}
